import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Play, Lock, ShoppingCart, ChevronRight, ChevronLeft, X, SkipForward } from 'lucide-react';
import { VideoPlayer } from '../components/VideoPlayer';
import { filmsApi, premiumContentApi, seriesApi, relatedFilmsApi, episodesApi, thumbUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PaymentMethodModal from '../components/PaymentMethodModal';

const WatchPage = () => {
  const { filmId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTrailer = searchParams.get('trailer') === 'true';
  const episodeParam = searchParams.get('episode');
  const purchaseSuccess = searchParams.get('purchase') === 'success';
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAds, setShowAds] = useState(false);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const [scheduledBlocked, setScheduledBlocked] = useState(false);
  const [verifyingPurchase, setVerifyingPurchase] = useState(false);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [showEpisodeList, setShowEpisodeList] = useState(false);
  const [showPremiumPurchaseModal, setShowPremiumPurchaseModal] = useState(false);

  // New states for end-of-video features
  const [relatedFilms, setRelatedFilms] = useState([]);
  const [showNextEpOverlay, setShowNextEpOverlay] = useState(false);
  const [showRelatedOverlay, setShowRelatedOverlay] = useState(false);
  const [autoPlayCountdown, setAutoPlayCountdown] = useState(5);
  const countdownRef = useRef(null);
  const nearEndTriggeredRef = useRef(false);

  useEffect(() => {
    const fetchFilm = async () => {
      try {
        const data = await filmsApi.getById(filmId);
        setFilm(data);

        // Fetch related films
        relatedFilmsApi.get(filmId).then(r => setRelatedFilms(r.films || [])).catch(() => {});

        if (data.content_type === 'series') {
          try {
            const epData = await seriesApi.getEpisodes(filmId);
            const allEpisodes = epData.episodes || [];
            setEpisodes(allEpisodes);
            if (episodeParam) {
              const ep = allEpisodes.find(e => e.episode_id === episodeParam);
              setCurrentEpisode(ep || (allEpisodes.length > 0 ? allEpisodes[0] : null));
            } else if (allEpisodes.length > 0) {
              setCurrentEpisode(allEpisodes[0]);
            }
          } catch (e) {
            console.error('Failed to fetch episodes:', e);
          }
        }

        if (isTrailer) { setShowAds(false); return; }
        if (purchaseSuccess && sessionId && isAuthenticated) {
          setVerifyingPurchase(true);
          try {
            const purchaseStatus = await premiumContentApi.getPurchaseStatus(sessionId);
            if (purchaseStatus.status === 'completed' || purchaseStatus.payment_status === 'paid') {
              toast.success('Purchase successful! Enjoy your content.');
              setShowAds(false);
              setPremiumBlocked(false);
            } else {
              toast.error('Purchase not completed. Please try again.');
              navigate(`/film/${filmId}`);
              return;
            }
          } catch (e) {
            toast.error('Could not verify purchase.');
          }
          setVerifyingPurchase(false);
          return;
        }
        if (isAuthenticated) {
          try {
            const watchCheck = await filmsApi.canWatch(filmId);
            setShowAds(watchCheck.show_ads === true);
            if (watchCheck.reason === 'premium_content_not_purchased') setPremiumBlocked(true);
            if (watchCheck.reason === 'scheduled') setScheduledBlocked(true);
          } catch { setShowAds(true); }
        } else {
          if (data.is_scheduled) setScheduledBlocked(true);
          setShowAds(false);
        }
      } catch {
        navigate('/browse');
      } finally {
        setLoading(false);
      }
    };
    fetchFilm();
  }, [filmId, navigate, isAuthenticated, isTrailer, purchaseSuccess, sessionId, episodeParam]);

  // Cleanup visual countdown on unmount
  useEffect(() => {
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  const currentEpisodeIndex = episodes.findIndex(e => e.episode_id === currentEpisode?.episode_id);
  const nextEpisode = currentEpisodeIndex >= 0 && currentEpisodeIndex < episodes.length - 1
    ? episodes[currentEpisodeIndex + 1] : null;
  const prevEpisode = currentEpisodeIndex > 0 ? episodes[currentEpisodeIndex - 1] : null;
  const isLastEpisode = currentEpisodeIndex >= 0 && currentEpisodeIndex === episodes.length - 1;

  const handleEpisodeSelect = useCallback((ep) => {
    setShowNextEpOverlay(false);
    setShowRelatedOverlay(false);
    setAutoPlayCountdown(5);
    if (countdownRef.current) clearInterval(countdownRef.current);
    nearEndTriggeredRef.current = false;
    setCurrentEpisode(ep);
    setSearchParams({ episode: ep.episode_id });
  }, [setSearchParams]);

  const goToNextEpisode = useCallback(() => {
    console.log('[WatchPage] goToNextEpisode called, nextEpisode:', nextEpisode?.episode_id);
    if (nextEpisode) {
      handleEpisodeSelect(nextEpisode);
    }
  }, [nextEpisode, handleEpisodeSelect]);

  // Callback when video time updates — only shows cosmetic overlay, never interferes with playback
  const handleTimeProgress = useCallback((currentTime, duration) => {
    if (!film || isTrailer) return;
    const remaining = duration - currentTime;
    const isSeries = film.content_type === 'series' && currentEpisode;

    if (remaining > 10) {
      nearEndTriggeredRef.current = false;
    }

    // Log during last 6 seconds for debugging
    if (isSeries && remaining <= 6 && remaining > 0) {
      console.log(`[WatchPage] timeProgress: remaining=${remaining.toFixed(2)}s nearEndTriggered=${nearEndTriggeredRef.current} overlay=${showNextEpOverlay}`);
    }

    // Show cosmetic "up next" overlay at T-5s — purely informational, no action taken
    if (isSeries && nextEpisode && remaining <= 5 && remaining > 0 && !nearEndTriggeredRef.current) {
      console.log('[WatchPage] Showing next episode overlay (cosmetic only)');
      nearEndTriggeredRef.current = true;
      setShowNextEpOverlay(true);
      // Visual-only countdown (does NOT trigger episode transition)
      setAutoPlayCountdown(Math.ceil(remaining));
      if (countdownRef.current) clearInterval(countdownRef.current);
      countdownRef.current = setInterval(() => {
        setAutoPlayCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [film, isTrailer, currentEpisode, nextEpisode, showNextEpOverlay]);

  // THE SINGLE SOURCE OF TRUTH: video ended event triggers next episode
  const handleVideoEnded = useCallback(() => {
    console.log('[WatchPage] handleVideoEnded called', { isSeries: film?.content_type === 'series', hasNext: !!nextEpisode });
    if (!film || isTrailer) return;
    const isSeries = film.content_type === 'series' && currentEpisode;

    // Clean up any visual countdown
    if (countdownRef.current) clearInterval(countdownRef.current);
    setShowNextEpOverlay(false);

    if (isSeries && nextEpisode) {
      goToNextEpisode();
    } else {
      setShowRelatedOverlay(true);
    }
  }, [film, isTrailer, currentEpisode, nextEpisode, goToNextEpisode]);

  // Smart back button - navigate back replacing history so watch page doesn't stay in stack
  const handleBack = useCallback(() => {
    navigate(`/film/${filmId}`, { replace: true });
  }, [navigate, filmId]);

  // Track views — episodes and films (standalone)
  useEffect(() => {
    if (isTrailer) return;
    if (currentEpisode) {
      episodesApi.incrementView(currentEpisode.episode_id).catch(() => {});
    } else if (film && film.content_type !== 'series') {
      filmsApi.incrementView(filmId).catch(() => {});
    }
  }, [currentEpisode, isTrailer, film, filmId]);

  // Memoize videoData to prevent unnecessary re-renders of VideoPlayer.
  // Without this, every WatchPage state change (overlay, countdown) creates a new object
  // which causes VideoPlayer effects (restoreProgress) to re-run and seek the video.
  const videoData = useMemo(() => {
    if (!film) return null;
    if (isTrailer) {
      return { ...film, title: `${film.title} - Trailer` };
    } else if (film.content_type === 'series' && currentEpisode) {
      return {
        ...film,
        video_file: currentEpisode.video_file,
        title: `${film.title} - S${currentEpisode.season_number}E${currentEpisode.episode_number}: ${currentEpisode.title}`,
      };
    }
    return film;
  }, [film, isTrailer, currentEpisode]);

  if (loading || verifyingPurchase) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4" />
          {verifyingPurchase && <p className="text-gray-400">Verifying your purchase...</p>}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#030014]" data-testid="auth-required">
        <div className="absolute inset-0">
          {film?.thumbnail && (
            <>
              <div className="absolute inset-0 bg-cover bg-center opacity-20 blur-xl" style={{ backgroundImage: `url(${thumbUrl(film.thumbnail, 480)})` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/60" />
            </>
          )}
        </div>
        <div className="absolute top-6 left-6 z-20">
          <button onClick={() => navigate(`/film/${filmId}`)} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" /> Back
          </button>
        </div>
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <div className="admin-card max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
              <Lock className="w-10 h-10 text-[#8B5CF6]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Sign in to watch</h1>
            <p className="text-gray-400 mb-6 text-sm">Create a free account or sign in to start watching content on reframes.</p>
            {film && (
              <div className="mb-6">
                <img src={film.thumbnail} alt={film.title} className="w-full max-w-xs mx-auto rounded-lg mb-3" />
                <h2 className="text-lg font-semibold">{film.title}</h2>
              </div>
            )}
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate('/login', { state: { from: `/watch/${filmId}` } })} className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-11" data-testid="auth-login-btn">Sign In</Button>
              <Button onClick={() => navigate('/register', { state: { from: `/watch/${filmId}` } })} variant="outline" className="w-full h-11" data-testid="auth-register-btn">Create Free Account</Button>
            </div>
            <p className="text-sm text-gray-500 mt-4"><Link to="/browse" className="text-[#8B5CF6] hover:underline">Continue browsing</Link></p>
          </div>
        </div>
      </div>
    );
  }

  if (premiumBlocked && film) {
    return (
      <div className="min-h-screen bg-[#030014]" data-testid="premium-blocked">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center opacity-20 blur-xl" style={{ backgroundImage: `url(${film.thumbnail})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/60" />
        </div>
        <div className="absolute top-6 left-6 z-20">
          <button onClick={() => navigate(`/film/${filmId}`)} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-button"><ArrowLeft className="w-5 h-5" /> Back</button>
        </div>
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <div className="admin-card max-w-lg w-full p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center"><Lock className="w-10 h-10 text-yellow-400" /></div>
            <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Premium Content</h1>
            <div className="mb-6">
              <img src={film.thumbnail} alt={film.title} className="w-full max-w-sm mx-auto rounded-lg mb-4" />
              <h2 className="text-lg font-semibold">{film.title}</h2>
              <p className="text-sm text-gray-400">{film.creator}</p>
            </div>
            <p className="text-gray-400 mb-6">This is exclusive pay-per-view content. Purchase once to watch anytime.</p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-2xl font-bold text-yellow-400">{film.premium_price?.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">One-time purchase - Lifetime access</p>
            </div>
            <Button onClick={() => setShowPremiumPurchaseModal(true)} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black h-12" data-testid="purchase-premium-btn"><ShoppingCart className="w-5 h-5 mr-2" /> Purchase Now</Button>
            <p className="text-sm text-gray-500 mt-4"><Link to={`/film/${filmId}`} className="text-[#8B5CF6] hover:underline">View details</Link></p>
          </div>
        </div>
        <PaymentMethodModal
          isOpen={showPremiumPurchaseModal}
          onClose={() => setShowPremiumPurchaseModal(false)}
          paymentType="premium_content"
          amount={film.premium_price || 0}
          filmId={filmId}
          creatorName={film.creator}
          onSuccess={() => { setPremiumBlocked(false); setShowPremiumPurchaseModal(false); }}
        />
      </div>
    );
  }

  if (scheduledBlocked && film) {
    const releaseDate = film.release_date ? new Date(film.release_date + 'T00:00:00') : null;
    return (
      <div className="min-h-screen bg-[#030014]" data-testid="scheduled-blocked">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center opacity-20 blur-xl" style={{ backgroundImage: `url(${film.thumbnail})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/60" />
        </div>
        <div className="absolute top-6 left-6 z-20">
          <button onClick={() => navigate(`/film/${filmId}`)} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-button"><ArrowLeft className="w-5 h-5" /> Back</button>
        </div>
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <div className="admin-card max-w-lg w-full p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center"><Play className="w-10 h-10 text-[#8B5CF6]" /></div>
            <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Coming Soon</h1>
            <div className="mb-6">
              <img src={film.thumbnail} alt={film.title} className="w-full max-w-sm mx-auto rounded-lg mb-4" />
              <h2 className="text-lg font-semibold">{film.title}</h2>
              <p className="text-sm text-gray-400">{film.creator}</p>
            </div>
            {releaseDate && (
              <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-lg p-4 mb-6">
                <p className="text-xs text-gray-400 mb-1">Releases on</p>
                <p className="text-xl font-bold text-[#8B5CF6]">{releaseDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
            )}
            <p className="text-gray-400 mb-6">This content has been scheduled by the creator and is not available yet.</p>
            <Button onClick={() => navigate(`/film/${filmId}`)} variant="outline" className="w-full h-12" data-testid="view-details-btn">View Details</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !isTrailer) {
    return (
      <div className="min-h-screen bg-[#030014]" data-testid="login-prompt">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center opacity-20 blur-xl" style={{ backgroundImage: `url(${film?.thumbnail})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/60" />
        </div>
        <div className="absolute top-6 left-6 z-20">
          <button onClick={() => navigate(`/film/${filmId}`)} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-button"><ArrowLeft className="w-5 h-5" /> Back</button>
        </div>
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <div className="admin-card max-w-lg w-full p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center"><Play className="w-10 h-10 text-[#8B5CF6]" /></div>
            <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Sign In to Watch</h1>
            {film && (
              <div className="mb-6">
                <img src={film.thumbnail} alt={film.title} className="w-full max-w-sm mx-auto rounded-lg mb-4" />
                <h2 className="text-lg font-semibold">{film.title}</h2>
                <p className="text-sm text-gray-400">{film.creator}</p>
              </div>
            )}
            <p className="text-gray-400 mb-8">Create a free account to start watching. No credit card required.</p>
            <Link to="/login" className="block"><Button className="w-full btn-white h-12" data-testid="sign-in-to-watch"><Play className="w-5 h-5 mr-2" /> Sign In to Watch Free</Button></Link>
            <p className="text-sm text-gray-500 mt-4">Don't have an account? <Link to="/register" className="text-[#8B5CF6] hover:underline">Sign up free</Link></p>
          </div>
        </div>
      </div>
    );
  }

  if (film.content_type === 'series' && !currentEpisode && !isTrailer) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="no-episodes">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-4">No episodes available for this series yet.</p>
          <button onClick={() => navigate(`/film/${filmId}`)} className="text-[#8B5CF6] hover:underline">Back to series details</button>
        </div>
      </div>
    );
  }

  // Determine analyticsId for episode-level tracking
  const analyticsId = (film.content_type === 'series' && currentEpisode) ? currentEpisode.episode_id : undefined;

  return (
    <div className="relative" data-testid="watch-page">
      <VideoPlayer
        key={currentEpisode?.episode_id || filmId}
        film={videoData}
        showAds={showAds}
        hasEpisodeBar={film.content_type === 'series' && episodes.length > 1}
        onVideoEnded={handleVideoEnded}
        onTimeProgress={handleTimeProgress}
        onBack={handleBack}
        analyticsId={analyticsId}
      />

      {/* ── Next Episode Overlay (last 5 seconds) ── */}
      {showNextEpOverlay && nextEpisode && (
        <div className="fixed bottom-20 right-4 md:right-8 z-[60] animate-in slide-in-from-right" data-testid="next-episode-overlay">
          <div className="bg-black/90 backdrop-blur-md border border-white/10 rounded-xl p-4 w-72 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Up Next</span>
              <button
                onClick={() => { setShowNextEpOverlay(false); if (countdownRef.current) clearInterval(countdownRef.current); }}
                className="text-gray-500 hover:text-white transition-colors"
                data-testid="dismiss-next-ep"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-20 h-12 rounded-md overflow-hidden bg-white/5 shrink-0">
                <img src={film.thumbnail} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">S{nextEpisode.season_number}E{nextEpisode.episode_number}</p>
                <p className="text-xs text-gray-400 truncate">{nextEpisode.title}</p>
              </div>
            </div>
            <button
              onClick={goToNextEpisode}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black font-semibold rounded-lg hover:bg-gray-200 transition-colors text-sm"
              data-testid="play-next-episode-btn"
            >
              <SkipForward className="w-4 h-4" />
              Play Next ({autoPlayCountdown}s)
            </button>
          </div>
        </div>
      )}

      {/* ── Related Content Overlay (end of movie or last episode) ── */}
      {showRelatedOverlay && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end" data-testid="related-overlay">
          <div className="w-full bg-gradient-to-t from-[#030014] via-[#030014]/95 to-transparent p-6 md:p-8 pb-8 max-h-[70vh] overflow-y-auto">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {isLastEpisode ? 'Series Complete' : 'What to Watch Next'}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {isLastEpisode
                      ? `You've finished all episodes of ${film.title}`
                      : `Based on "${film.title}"`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/film/${filmId}`)}
                    className="px-4 py-2 text-sm bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    data-testid="related-back-to-details"
                  >
                    Back to Details
                  </button>
                  <button
                    onClick={() => setShowRelatedOverlay(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    data-testid="close-related-overlay"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {relatedFilms.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {relatedFilms.slice(0, 12).map(rf => (
                    <button
                      key={rf.film_id}
                      onClick={() => navigate(`/film/${rf.film_id}`)}
                      className="group text-left"
                      data-testid={`related-film-${rf.film_id}`}
                    >
                      <div className="aspect-video rounded-lg overflow-hidden mb-2 bg-white/5">
                        <img src={rf.thumbnail} alt={rf.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" loading="lazy" />
                      </div>
                      <p className="text-xs font-medium truncate group-hover:text-[#8B5CF6] transition-colors">{rf.title}</p>
                      <p className="text-[10px] text-gray-500">{rf.creator}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No related content found</p>
                  <button onClick={() => navigate('/browse')} className="text-[#8B5CF6] hover:underline text-sm mt-2">Browse all content</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Episode navigation for series */}
      {film.content_type === 'series' && episodes.length > 1 && (
        <>
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black/95 to-black/70 backdrop-blur-sm px-4 py-3" data-testid="episode-nav">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <button
                onClick={() => prevEpisode && handleEpisodeSelect(prevEpisode)}
                disabled={!prevEpisode}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${prevEpisode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-white/5 text-gray-600 cursor-not-allowed'}`}
                data-testid="prev-episode-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <button
                onClick={() => setShowEpisodeList(!showEpisodeList)}
                className="px-4 py-2 bg-[#8B5CF6]/20 hover:bg-[#8B5CF6]/30 text-[#A78BFA] rounded-lg text-sm transition-colors"
                data-testid="toggle-episode-list-btn"
              >
                S{currentEpisode?.season_number}E{currentEpisode?.episode_number} - {episodes.length} episodes
              </button>
              <button
                onClick={() => nextEpisode && handleEpisodeSelect(nextEpisode)}
                disabled={!nextEpisode}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${nextEpisode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-white/5 text-gray-600 cursor-not-allowed'}`}
                data-testid="next-episode-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showEpisodeList && (
            <div className="fixed right-0 top-0 bottom-16 w-80 z-50 bg-[#0F0B1E]/95 backdrop-blur-md border-l border-white/10 overflow-y-auto" data-testid="episode-list-panel">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm text-gray-300">Episodes</h3>
                  <button onClick={() => setShowEpisodeList(false)} className="text-gray-400 hover:text-white text-sm">Close</button>
                </div>
                <div className="space-y-1">
                  {episodes.map((ep) => (
                    <button
                      key={ep.episode_id}
                      onClick={() => { handleEpisodeSelect(ep); setShowEpisodeList(false); }}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${ep.episode_id === currentEpisode?.episode_id ? 'bg-[#8B5CF6]/20 border border-[#8B5CF6]/40' : 'hover:bg-white/5'}`}
                      data-testid={`episode-list-item-${ep.episode_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center ${ep.episode_id === currentEpisode?.episode_id ? 'bg-[#8B5CF6] text-white' : 'bg-white/10 text-gray-400'}`}>
                          {ep.episode_number}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">S{ep.season_number}E{ep.episode_number}: {ep.title}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WatchPage;
